import { useSyncExternalStore } from 'react'

import { F0CanvasCard } from '@factorialco/f0-react'

import i18n from 'modules/core/lib/i18n'

import { useAutoOpenCanvas } from 'modules/ai/copilotActions/canvas/hooks/useAutoOpenCanvas'
import { savedDashboardConfigStore, savedDashboardMetaStore } from './configStore'
import { isSingleCollectionDashboard } from './DashboardContent'
import type { DashboardRuntimeConfig } from './types'

export type DashboardCardProps = {
  /** The original dashboard config from the agent */
  config: DashboardRuntimeConfig
  /** API config for server-side dashboard computation */
  apiConfig: {
    baseUrl: string
    headers: Record<string, string>
    runtimeFetch?: typeof fetch
  }
  /** Present when the dashboard is a pre-saved dashboard */
  savedDashboardId?: string
  /** Category of the saved dashboard */
  savedDashboardCategory?: string
  /** Description of the saved dashboard */
  savedDashboardDescription?: string
  /** Whether the dashboard has unsaved changes */
  savedDashboardUnsaved?: boolean
  /**
   * Unique id used for per-card config-store keys. Pass the CopilotKit
   * toolCallId from the surrounding render context.
   */
  cardId: string | undefined
  /** Whether this card is currently open in the canvas. */
  isActive: boolean
  /** Open the canvas with this dashboard. */
  onOpen: (resolved: {
    config: DashboardRuntimeConfig
    savedDashboardId?: string
    savedDashboardCategory?: string
    savedDashboardDescription?: string
    savedDashboardUnsaved?: boolean
  }) => void
  /** Close the canvas. */
  onClose: () => void
  /**
   * Optional key for one-shot auto-open. Defaults to `cardId`.
   */
  autoOpenKey?: string
}

/**
 * Dashboard-specific card that wraps F0CanvasCard with config-store
 * subscription logic. Re-renders when the user edits and saves the
 * dashboard layout. Decoupled from F0AiChat — open/close come via props.
 */
export function DashboardCard({
  config: originalConfig,
  apiConfig: _apiConfig,
  savedDashboardId: propSavedId,
  savedDashboardCategory: propSavedCategory,
  savedDashboardDescription: propSavedDescription,
  savedDashboardUnsaved: propSavedUnsaved = false,
  cardId,
  isActive,
  onOpen,
  onClose,
  autoOpenKey,
}: DashboardCardProps) {
  useSyncExternalStore(savedDashboardConfigStore.subscribe, savedDashboardConfigStore.getSnapshot)
  useSyncExternalStore(savedDashboardMetaStore.subscribe, savedDashboardMetaStore.getSnapshot)

  const config = (cardId ? savedDashboardConfigStore.get(cardId) : undefined) ?? originalConfig

  const meta = cardId ? savedDashboardMetaStore.get(cardId) : undefined
  const savedDashboardId = meta?.savedDashboardId ?? propSavedId
  const savedDashboardCategory = meta?.savedDashboardCategory ?? propSavedCategory
  const savedDashboardDescription = meta?.savedDashboardDescription ?? propSavedDescription
  const savedDashboardUnsaved = meta?.savedDashboardUnsaved ?? propSavedUnsaved

  const handleOpen = () =>
    onOpen({
      config,
      savedDashboardId,
      savedDashboardCategory,
      savedDashboardDescription,
      savedDashboardUnsaved,
    })

  useAutoOpenCanvas(autoOpenKey ?? cardId, handleOpen)

  // Use "Table" when this is really a single-collection dashboard
  // (tables-as-dashboard flow). Otherwise keep "Report".
  const cardDescription = isSingleCollectionDashboard(config)
    ? i18n.t('common.ai.report_card.table_label')
    : i18n.t('common.ai.report_card.report_label')

  return (
    <F0CanvasCard
      module="analytics"
      title={config.title}
      description={cardDescription}
      onOpen={handleOpen}
      onClose={onClose}
      isActive={isActive}
    />
  )
}
